/**
 * DeviceVerifyPage — CLI / IDE device authorization gate
 *
 * Mounted at /device/verify. The CLI prints this URL with ?user_code=XXXX-XXXX.
 *
 * Flow:
 *   Logged in  → show code, confirm button → POST /v2/device/confirm (API key auth)
 *   Not logged in → prompt to log in first (no Keycloak redirect from this page)
 *
 * @param user_code - Pre-filled from query string (XXXX-XXXX format)
 */

import { useState } from 'react';
import { ShieldCheck, AlertCircle, Loader2, Terminal, LogIn, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { useAuth } from '@/contexts/AuthContext';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/** SessionStorage key for persisting device verify URL across the login flow */
export const DEVICE_PENDING_KEY = 'guardrail:device:pending';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function DeviceVerifyPage() {
  useDocumentTitle('Verify Device');
  const params = new URLSearchParams(window.location.search);
  const prefilled = params.get('user_code') ?? '';

  const [userCode, setUserCode] = useState(prefilled);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { authenticated, apiKey } = useAuth();
  const isLoggedIn = authenticated && !!apiKey;

  async function handleAuthorize() {
    const code = userCode.trim().toUpperCase();
    if (!code.match(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/)) {
      setError('Enter a valid code in XXXX-XXXX format.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resp = await fetch(`${API_URL}/v2/device/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey!,
        },
        body: JSON.stringify({ user_code: code }),
      });

      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({}))) as { message?: string };
        throw new Error(detail.message ?? `Server error ${resp.status}`);
      }

      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleCodeInput(e: React.ChangeEvent<HTMLInputElement>) {
    let val = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (val.length > 4) val = val.slice(0, 4) + '-' + val.slice(4, 8);
    setUserCode(val);
    setError(null);
  }

  // ── Success screen ──────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
        <Card className="w-full max-w-md bg-zinc-800/60 border-zinc-700 backdrop-blur text-center">
          <CardHeader className="space-y-4">
            <div className="flex justify-center"><TrafficLightLogo size="md" /></div>
            <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" />
            <CardTitle className="text-white text-2xl">Authorization Complete</CardTitle>
            <CardDescription className="text-zinc-400">
              Your CLI is now authenticated. You can close this window and return to your terminal.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // ── Not logged in ───────────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
        <Card className="w-full max-w-md bg-zinc-800/60 border-zinc-700 backdrop-blur">
          <CardHeader className="text-center space-y-3 pb-2">
            <div className="flex justify-center"><TrafficLightLogo size="md" /></div>
            <CardTitle className="text-white text-2xl">CLI Authorization</CardTitle>
            <CardDescription className="text-zinc-400">
              You need to be logged in to authorize your CLI.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            {prefilled && (
              <div className="flex items-center justify-center rounded-xl border border-zinc-600 bg-zinc-900/60 py-3">
                <Terminal className="w-4 h-4 text-zinc-400 mr-2 shrink-0" />
                <span className="font-mono text-xl font-bold tracking-widest text-zinc-300">
                  {prefilled}
                </span>
              </div>
            )}
            <a
              href={`/login?return=${encodeURIComponent(window.location.pathname + window.location.search)}`}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3 text-base font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/20"
            >
              <LogIn className="h-4 w-4" />
              Log in to continue
            </a>
            <p className="text-center text-xs text-zinc-500">
              After logging in you will be returned here to complete authorization.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Logged in: confirm the device code ─────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
      <Card className="w-full max-w-md bg-zinc-800/60 border-zinc-700 backdrop-blur">
        <CardHeader className="text-center space-y-3 pb-2">
          <div className="flex justify-center"><TrafficLightLogo size="md" /></div>
          <CardTitle className="text-white text-2xl">CLI Authorization</CardTitle>
          <CardDescription className="text-zinc-400">
            {prefilled
              ? 'Confirm this code matches your terminal, then authorize.'
              : 'Enter the code displayed in your terminal.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5 pt-4">
          {prefilled ? (
            <div className="flex items-center justify-center rounded-xl border-2 border-green-500 bg-green-500/10 py-4">
              <Terminal className="w-5 h-5 text-green-400 mr-3 shrink-0" />
              <span className="font-mono text-3xl font-bold tracking-[0.25em] text-green-300">
                {userCode}
              </span>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Device Code</label>
              <input
                type="text"
                value={userCode}
                onChange={handleCodeInput}
                placeholder="XXXX-XXXX"
                maxLength={9}
                autoFocus
                className="w-full rounded-lg border border-zinc-600 bg-zinc-900 px-4 py-3 text-center font-mono text-xl tracking-widest text-white uppercase placeholder:text-zinc-600 focus:border-green-500 focus:outline-none"
              />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleAuthorize}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 text-base font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/20 disabled:opacity-60 disabled:transform-none"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {loading ? 'Authorizing…' : 'Authorize CLI'}
          </button>

          <p className="text-center text-xs text-zinc-500">
            🔒 Only authorize if you initiated this from your terminal or IDE.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
